/* eslint-disable @typescript-eslint/no-use-before-define */
import classnames from 'classnames';
import { differenceInMinutes } from 'date-fns';
import cloneDeep from 'lodash.clonedeep';
import { intersection } from 'ramda';
import React, { Dispatch, RefObject, useState, useRef, useEffect, useMemo } from 'react';
import { Popover } from 'reactstrap';

import { Skill } from '../../../../graphql/components';
import { Agent } from '../../../Agents/types';
import {
  ChartScheduleElement,
  DaySettings,
  ElementIdsPerAgent,
  ProductionChartScheduleElement,
  QueuesWithColors,
} from '../../types';
import { emptyAgentsData } from '../../utils/mapChartData';
import { CloneScheduleMenu } from '../CloneScheduleMenu';
import {
  Action,
  clearSchedule,
  cloneSchedule,
  copySchedule,
  pasteSchedule,
  pinAllScheduleElements,
  pinElementsInCurrentDay,
  pinSelectedElements,
  removeAgents,
  selectAll,
  unpinAllScheduleElements,
  unpinElementsInCurrentDay,
  unpinSelectedElements,
  updateAgentDaySetting,
} from '../actions';
import { unselectBrowserSelection } from '../layoutUtils';
import { YLabel, emptyScheduledHours, ScheduleHours } from './YLabel';

export interface YLabelListProps {
  agents: Record<string, Agent>;
  cloneMenuOpened: boolean;
  containerRef: RefObject<HTMLDivElement>;
  daySettings: DaySettings;
  daySettingsActive: boolean;
  dispatch: Dispatch<Action>;
  elementIdsPerAgent: Record<string, ElementIdsPerAgent>;
  isAutoScheduleLimitExceeded: boolean;
  isContextMenuEnabled: boolean;
  isMultiQueueSchedule: boolean;
  isPasteScheduleActive: boolean;
  lockedAgentsIds: Record<string, boolean>;
  maxDate: Date;
  minDate: Date;
  pickedTime: Date;
  productionScheduleElements: ProductionChartScheduleElement[];
  queues: QueuesWithColors;
  scrollbarContainerRef: HTMLElement | null;
  selectedAgents: string[];
  setCloneMenuOpened: (state: boolean) => void;
  skills: Record<string, Skill>;
  triggerCreateAutoScheduleJob?: (selectedAgentIds?: string[]) => Promise<void>;
  usedScheduleElements: ChartScheduleElement[];
}

type DataPerAgent = ElementIdsPerAgent & {
  scheduledHours: ScheduleHours;
  usedDraftElementsCount: number;
};
type DurationPerAgentPropType = Partial<Record<string, DataPerAgent>>;

export const calculateScheduledHoursPerAgent = (
  usedScheduleElements: ChartScheduleElement[],
  productionElements: ProductionChartScheduleElement[]
) => {
  const agentsData: DurationPerAgentPropType = {};

  const elements = [...productionElements, ...usedScheduleElements] as ProductionChartScheduleElement[];

  elements.forEach(({ agentId, end, start, productive, selected, pinnable, pinned, key, sourceScheduleId }) => {
    if (agentId === undefined) {
      return;
    }

    // Draft elements should have that field not defined
    const isDraftElement = sourceScheduleId === undefined;

    const updatedAgentData = agentsData[agentId] || {
      ...cloneDeep(emptyAgentsData),
      scheduledHours: emptyScheduledHours,
      usedDraftElementsCount: 0,
    };

    const elementDuration = differenceInMinutes(end, start);

    updatedAgentData.scheduledHours = {
      ...updatedAgentData.scheduledHours,
      totalDuration: updatedAgentData.scheduledHours.totalDuration + elementDuration,
      ...(productive && {
        workDuration: updatedAgentData.scheduledHours.workDuration + elementDuration,
      }),
    };

    if (isDraftElement) {
      updatedAgentData.usedDraftElementsCount += 1;
      // These should be calculated only for draft elements, so we can manipulate with them through context menu
      if (selected) {
        updatedAgentData.selected.push(key);
      }

      if (pinned) {
        updatedAgentData.pinned.push(key);
      }

      if (pinnable) {
        updatedAgentData.pinnable.push(key);
      }
    }

    agentsData[agentId] = updatedAgentData;
  });

  return agentsData;
};

export const YLabelList: React.FC<YLabelListProps> = ({
  agents,
  cloneMenuOpened,
  containerRef,
  daySettings,
  daySettingsActive,
  dispatch,
  elementIdsPerAgent,
  isAutoScheduleLimitExceeded,
  isContextMenuEnabled,
  isMultiQueueSchedule,
  isPasteScheduleActive,
  lockedAgentsIds,
  maxDate,
  minDate,
  pickedTime,
  productionScheduleElements,
  queues,
  scrollbarContainerRef,
  selectedAgents,
  setCloneMenuOpened,
  skills,
  triggerCreateAutoScheduleJob,
  usedScheduleElements,
}) => {
  const cloneMenuRef = useRef<HTMLDivElement>(null);

  const [isContainerAttached, setContainerAttached] = useState(false);

  const agentsData = useMemo(
    () => calculateScheduledHoursPerAgent(usedScheduleElements, productionScheduleElements),
    [productionScheduleElements, usedScheduleElements]
  );
  const cloneMenuTarget = scrollbarContainerRef || containerRef.current!;

  // To prevent duplicate 'mousedown' listeners
  useEffect(() => {
    if (cloneMenuOpened) {
      document.addEventListener('mousedown', handleCloneMenuDismiss);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cloneMenuOpened]);

  useEffect(() => {
    setContainerAttached(true);
  }, []);

  const hideCloneMenu = () => {
    setCloneMenuOpened(false);
    document.removeEventListener('mousedown', handleCloneMenuDismiss);
  };

  const handleCloneMenuDismiss = (e: Event) => {
    if (cloneMenuRef && cloneMenuRef.current && e.target && !cloneMenuRef.current.contains(e.target as Node)) {
      hideCloneMenu();
    }
    unselectBrowserSelection();
  };

  return (
    <>
      <div
        className={classnames('gantt-y-label__container', {
          'gantt-y-label__container--forced-open': cloneMenuOpened,
        })}
        data-test-id="gantt-y-label-list"
        ref={containerRef}
      >
        {selectedAgents.map((agentId, indexOfYLabel) => {
          const agent = agents[agentId];
          const data = agentsData[agent.id];
          const pinnedAndSelectedElementIds = data ? intersection(data.pinned, data.selected) : [];
          const pinnableAndSelectedElementIds = data ? intersection(data.pinnable, data.selected) : [];
          const usedElementsCount = data?.usedDraftElementsCount ?? 0;

          const isAgentInValidStateForAutoScheduleJob =
            !agent.isNotAssigned && !agent.deleted && agent.active && !!agent.shiftTemplateIds?.length;

          return (
            <YLabel
              agent={agent}
              agentDaySettings={daySettings.agents[agent.id]}
              clearSchedule={usedElementsCount ? () => dispatch(clearSchedule(agent.id)) : undefined}
              cloneSchedule={
                usedElementsCount
                  ? (selectedDates: Date[]) => dispatch(cloneSchedule([agent.id], selectedDates))
                  : undefined
              }
              copySchedule={usedElementsCount ? () => dispatch(copySchedule(agent.id)) : undefined}
              daySettingsActive={daySettingsActive}
              index={indexOfYLabel}
              isContextMenuEnabled={isContextMenuEnabled}
              isMultiQueueSchedule={isMultiQueueSchedule}
              key={agent.id}
              maxDate={maxDate}
              minDate={minDate}
              pasteSchedule={isPasteScheduleActive ? () => dispatch(pasteSchedule(agent.id, indexOfYLabel)) : undefined}
              pickedTime={pickedTime}
              pinAllScheduleElements={() => dispatch(pinAllScheduleElements(agent.id))}
              pinElementsInCurrentDay={() => dispatch(pinElementsInCurrentDay(agent.id))}
              pinnableElementsCount={elementIdsPerAgent[agent.id]?.pinnable.length ?? 0}
              pinnableElementsInCurrentDayCount={data?.pinnable.length ?? 0}
              pinnableSelectedElementsInCurrentDayCount={pinnableAndSelectedElementIds.length}
              pinnedElementsCount={elementIdsPerAgent[agent.id]?.pinned.length ?? 0}
              pinnedElementsInCurrentDayCount={data?.pinned.length ?? 0}
              pinnedSelectedElementsInCurrentDayCount={pinnedAndSelectedElementIds.length}
              pinSelectedElements={() => dispatch(pinSelectedElements(agent.id))}
              queues={queues}
              removeAgent={lockedAgentsIds[agent.id] ? undefined : () => dispatch(removeAgents([agent.id]))}
              scrollbarContainerRef={scrollbarContainerRef}
              selectAll={usedElementsCount ? () => dispatch(selectAll(agent.id)) : undefined}
              scheduledHours={data?.scheduledHours}
              skills={skills}
              triggerCreateAutoScheduleJob={
                isAgentInValidStateForAutoScheduleJob && triggerCreateAutoScheduleJob
                  ? triggerCreateAutoScheduleJob
                  : undefined
              }
              unpinAllScheduleElements={() => dispatch(unpinAllScheduleElements(agent.id))}
              unpinElementsInCurrentDay={() => dispatch(unpinElementsInCurrentDay(agent.id))}
              unpinSelectedElements={() => dispatch(unpinSelectedElements(agent.id))}
              workplaceChange={() =>
                dispatch(
                  updateAgentDaySetting(agent.id, daySettings.dayIndex, 'remote', !daySettings.agents[agent.id].remote)
                )
              }
              isAutoScheduleLimitExceeded={isAutoScheduleLimitExceeded}
            />
          );
        })}
      </div>

      {cloneMenuOpened && isContainerAttached && (
        <Popover
          isOpen={cloneMenuOpened}
          target={cloneMenuTarget}
          placement="top"
          fade={false}
          boundariesElement="window"
          className="clone-menu"
        >
          <CloneScheduleMenu
            cloneSchedule={(selectedDates: Date[]) => dispatch(cloneSchedule([], selectedDates))}
            handleCancel={hideCloneMenu}
            maxDate={maxDate}
            minDate={minDate}
            pickedTime={pickedTime}
            snackbarMessage="schedule.clonedAll"
            wrapperRef={cloneMenuRef}
          />
        </Popover>
      )}
    </>
  );
};
